// Статус «точка + слово» (задача D5, ui-spec §4): статус никогда не передаётся одним цветом — слово видно всегда,
// точка для чтения с экрана скрыта (`aria-hidden`), подпись обязательна по типам.
//
// Тоны (контракт `StatusTone`): `live` — синяя (ведёт, работает); `sales` — зелёная (в продажи, в порядке);
// `human` — янтарная (к человеку, внимание, экономит); `error` — красная (сбой, ошибка, нарушен срок);
// `reject` — серая (отказ — законный исход, остановлена); `neutral` — полая серая (ждёт, мало данных).
// `hollow` делает полой точку любого тона: «○ Остановлена», «○ ждёт: Ключ OpenAI».
import type { StatusTone } from '@contract/index.js';
import { cn } from 'cn';

export type { StatusTone };

const DOT: Readonly<Record<StatusTone, string>> = {
  live: 'bg-st-live-dot border-st-live-dot',
  sales: 'bg-st-sales-dot border-st-sales-dot',
  human: 'bg-st-human-dot border-st-human-dot',
  error: 'bg-st-error-dot border-st-error-dot',
  reject: 'bg-st-reject-dot border-st-reject-dot',
  neutral: 'bg-st-reject-dot border-st-reject-dot',
};

const WORD: Readonly<Record<StatusTone, string>> = {
  live: 'text-st-live',
  sales: 'text-st-sales',
  human: 'text-st-human',
  error: 'text-st-error',
  reject: 'text-st-reject',
  neutral: 'text-st-reject',
};

export interface StatusDotProps {
  readonly tone: StatusTone;
  /** Полая точка: остановлена, ждёт. У тона `neutral` точка полая всегда. */
  readonly hollow?: boolean;
  /** Слово статуса — видно всегда. */
  readonly label: string;
  /** `sm` — 6 px (пилюля, плотные списки), `md` — 8 px. */
  readonly size?: 'sm' | 'md';
  /** Вердикт экрана: слово цветом тона и полужирное. */
  readonly strong?: boolean;
  readonly className?: string;
}

/**
 * Одна точка без слова — только там, где слово рядом уже есть в подписи кнопки (`aria-label`): пилюля в сайдбаре
 * иконками. Для чтения с экрана скрыта.
 */
export function StatusMark({
  tone,
  hollow = false,
  size = 'md',
}: {
  readonly tone: StatusTone;
  readonly hollow?: boolean;
  readonly size?: 'sm' | 'md';
}) {
  const isHollow = hollow || tone === 'neutral';
  return (
    <span
      aria-hidden="true"
      data-hollow={isHollow ? '' : undefined}
      className={cn(
        'shrink-0 rounded-full border-[1.5px]',
        size === 'sm' ? 'size-1.5 border' : 'size-2',
        DOT[tone],
        isHollow && 'bg-transparent',
      )}
    />
  );
}

export function StatusDot({ tone, hollow = false, label, size = 'md', strong = false, className }: StatusDotProps) {
  return (
    <span
      data-slot="status-dot"
      data-tone={tone}
      className={cn('inline-flex min-w-0 items-center gap-1.5', strong && cn('font-semibold', WORD[tone]), className)}
    >
      <StatusMark tone={tone} hollow={hollow} size={size} />
      <span className="min-w-0">{label}</span>
    </span>
  );
}
